"use client";

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Progress } from "@/app/_module/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import axiosInstance from "@/app/_module/services/axiosConfig";

type GoalType = "premium" | "policies_submitted";
type Mode = "off" | "goal" | "all";

export interface BrandingAccess {
  /** enterprise: full editor; owner: name/logo/colors; locked: goal + progress; none: no tab. */
  access: "enterprise" | "owner" | "locked" | "none";
  canDelegate: boolean;
  uplineName: string | null;
  unlockedBy: "goal" | "upline" | "everyone" | null;
  unlockedAt: string | null;
  lockedByUpline: boolean;
  goal: { type: GoalType; value: number; thisMonth: number; lastMonth: number } | null;
  /** Set by us (admin); the sign-in page is only branded on one. */
  customDomain: string | null;
  /** Any agency under this one: the share switch only shows then. */
  hasAgenciesUnder: boolean;
}

interface ProgramAgency {
  id: string;
  name: string;
  depth: number;
  parentName: string | null;
  owners: string | null;
  hasBrand: boolean;
  thisMonth: number;
  lastMonth: number;
  decision: { state: "unlocked" | "locked"; by: "goal" | "upline"; at: string } | null;
  canBrand: boolean;
}

interface Program {
  mode: Mode;
  goal: { type: GoalType; value: number } | null;
  metric: GoalType;
  agencies: ProgramAgency[];
}

// The axios interceptor hands back the body; the API may wrap it in { statusCode, data } more than once.
function payload<T>(res: unknown): T {
  let d: any = res;
  while (d && typeof d === "object" && "statusCode" in d && "data" in d) d = d.data;
  return d as T;
}

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const amount = (type: GoalType, n: number) => (type === "premium" ? money(n) : n.toLocaleString("en-US"));
const goalText = (type: GoalType, n: number) =>
  type === "premium" ? `${money(n)} in premium` : `${n.toLocaleString("en-US")} ${n === 1 ? "policy" : "policies"}`;

/** What the current owner can do with their agency's brand. Shared by the tab gate and the Branding page. */
export function useBrandingAccess() {
  const { user, isInitializing } = useUserContext();
  const userId = user?.user?.id;
  const agencyId = user?.currentAgency?.agency?.id;
  // Sign-in's agency pick stores the token API's name for an owner, "agency_owner", and the
  // context keeps it until a reload. Exact "owner" never asked, so no tab right after
  // sign-in (Fedrick, 10-29). The API checks the real membership either way.
  const role = user?.currentAgency?.role;
  const isOwner = role === "owner" || role === "agency_owner";
  return useQuery<BrandingAccess>({
    queryKey: ["branding-access", userId, agencyId],
    queryFn: async () => payload<BrandingAccess>(await axiosInstance.get(`/api/branding/${agencyId}/access`)),
    // Wait for the profile, like the sidebar's queries. Fired while the session was still
    // restoring, it went out without a token, 401'd and never asked again (10-28: the
    // Agency Owners' Branding card only showed after a reload).
    enabled: !isInitializing && !!userId && !!agencyId && isOwner,
    // Asks again on every mount and window focus, so an unlock or lock shows without a reload.
    staleTime: 0,
    retry: 1,
  });
}

/**
 * Who gets the Branding tab: Enterprise owners, owners whose agency may brand, and owners
 * still working toward a goal (to see their progress). Locked with no goal to show, or
 * locked by hand, means no tab (owner, 10-29). The API checks the same.
 */
export function useCanEditBranding() {
  const { user } = useUserContext();
  const { data } = useBrandingAccess();
  const isEnterpriseOwner =
    user?.user?.accountType === "enterprise" &&
    (user?.currentAgency?.role === "owner" || user?.user?.userType === "agency_owner");
  const workingTowardGoal = data?.access === "locked" && !data.lockedByUpline && !!data.goal;
  return isEnterpriseOwner || data?.access === "owner" || workingTowardGoal;
}

/** A downline owner whose agency hasn't unlocked branding: the goal and how close they are. */
export function BrandingLockedCard({ access }: { access: BrandingAccess }) {
  const upline = access.uplineName || "Your upline";
  const goal = access.lockedByUpline ? null : access.goal;
  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 !text-lg">
          <Lock className="h-5 w-5 text-gray-500" />
          Agency branding is locked
        </CardTitle>
        <CardDescription>
          {goal
            ? `Write ${goalText(goal.type, goal.value)} in a calendar month to unlock it. Then you can put your agency's name, logo and colors on the app for everyone in your agency.`
            : `${upline} has branding locked for your agency, so your agents see ${upline}'s name, logo and colors.`}
        </CardDescription>
      </CardHeader>
      {goal && (
        <CardContent className="space-y-2">
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="font-medium">This month</span>
            <span className="tabular-nums">
              {amount(goal.type, goal.thisMonth)} of {amount(goal.type, goal.value)}
            </span>
          </div>
          <Progress value={Math.min(100, (goal.thisMonth / goal.value) * 100)} className="h-2" />
          <p className="text-xs text-muted-foreground">
            Counts sold and underwriting policies across your agency and the agencies under it, the same
            way Total Premium does on your agency dashboard. Once you hit it, branding stays unlocked.
          </p>
        </CardContent>
      )}
    </Card>
  );
}

/** One line on an unlocked owner's Branding page saying why they have it. */
export function unlockNote(access: BrandingAccess): string | null {
  const upline = access.uplineName || "Your upline";
  if (access.unlockedBy === "goal") {
    const when = access.unlockedAt ? new Date(access.unlockedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
    return `Unlocked by hitting ${upline}'s goal${when ? ` on ${when}` : ""}.`;
  }
  if (access.unlockedBy === "upline") return `Unlocked by ${upline}.`;
  if (access.unlockedBy === "everyone") return `${upline} lets every agency owner brand their agency.`;
  return null;
}

const MODE_OPTIONS: { value: Mode; label: string; detail: string }[] = [
  { value: "off", label: "Locked", detail: "Every agency under yours shows your name, logo and colors." },
  {
    value: "goal",
    label: "Unlock with a goal",
    detail: "An agency unlocks once it hits your goal in a calendar month. You can also unlock any agency yourself.",
  },
  { value: "all", label: "Unlocked for every agency", detail: "Every agency owner can brand their agency. You can still lock one." },
];

/**
 * The Enterprise owner decides whether the owners under them can brand their agency:
 * never, after a goal, or always, plus a per-agency unlock/lock. Changes here apply right
 * away, separate from the Save button for your own branding.
 */
export function OwnerBrandingProgramCard({ agencyId, brandName }: { agencyId: string; brandName: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["owner-branding", agencyId];
  const { data, isLoading } = useQuery<Program>({
    queryKey,
    queryFn: async () => payload<Program>(await axiosInstance.get(`/api/branding/${agencyId}/owner-branding`)),
    enabled: !!agencyId,
  });
  const [goalType, setGoalType] = useState<GoalType | null>(null);
  const [goalValue, setGoalValue] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const type = goalType ?? data?.goal?.type ?? "premium";
  const valueText = goalValue ?? (data?.goal ? String(data.goal.value) : "");
  const metric = data?.goal?.type ?? data?.metric ?? "premium";

  const run = async (key: string, request: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await request();
      await queryClient.invalidateQueries({ queryKey });
      toast({ title: done });
      return true;
    } catch (error: any) {
      toast({
        title: "Couldn't save",
        description: error?.response?.data?.message || error?.message || "Try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const setMode = (mode: Mode) =>
    run("mode", () => axiosInstance.put(`/api/branding/${agencyId}/owner-branding`, { mode }), `Agency owners' branding: ${MODE_OPTIONS.find((m) => m.value === mode)?.label}`);

  const saveGoal = async () => {
    const value = Number(valueText.replace(/[$,\s]/g, ""));
    if (!(value > 0)) {
      toast({ title: "Enter a goal above 0", variant: "destructive" });
      return;
    }
    const saved = await run(
      "goal",
      () => axiosInstance.put(`/api/branding/${agencyId}/owner-branding`, { mode: "goal", goalType: type, goalValue: value }),
      `Goal saved: ${goalText(type, value)} in a month`,
    );
    if (saved) {
      setGoalType(null);
      setGoalValue(null);
    }
  };

  const decide = (agency: ProgramAgency, state: "unlocked" | "locked") =>
    run(
      agency.id,
      () => axiosInstance.put(`/api/branding/${agencyId}/owner-branding/agencies/${agency.id}`, { state }),
      `${agency.name}: ${state === "unlocked" ? "unlocked" : "locked"}`,
    );

  const agencies = [...(data?.agencies ?? [])].sort((a, b) => b.thisMonth - a.thisMonth || a.name.localeCompare(b.name));
  const unlockedCount = agencies.filter((a) => a.canBrand).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="!text-base">Who can brand their agency</CardTitle>
        <CardDescription>
          Whether the owners of agencies under {brandName} can put their own name, logo and colors on the app for their
          agents. Changes here apply right away.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading || !data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading agencies…
          </div>
        ) : (
          <>
            <RadioGroup value={data.mode} onValueChange={(v) => setMode(v as Mode)} disabled={busy !== null} className="gap-3">
              {MODE_OPTIONS.map((option) => (
                <label key={option.value} htmlFor={`owner-branding-${option.value}`} className="flex cursor-pointer items-start gap-3">
                  <RadioGroupItem id={`owner-branding-${option.value}`} value={option.value} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.detail}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>

            {data.mode === "goal" && (
              <div className="space-y-2 rounded-lg bg-gray-50 p-4">
                <Label htmlFor="owner-branding-goal-value" className="text-sm font-medium">
                  Goal to unlock, in one calendar month
                </Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={type} onValueChange={(v) => setGoalType(v as GoalType)}>
                    <SelectTrigger className="w-40 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="policies_submitted">Policies</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="owner-branding-goal-value"
                    inputMode="numeric"
                    placeholder={type === "premium" ? "50000" : "40"}
                    value={valueText}
                    onChange={(e) => setGoalValue(e.target.value)}
                    className="w-36 bg-white"
                  />
                  <Button size="sm" onClick={saveGoal} disabled={busy !== null}>
                    {busy === "goal" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save goal"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.goal
                    ? `Now: ${goalText(data.goal.type, data.goal.value)} in a month. Counted like Total Premium on the agency dashboard: the agency and every agency under it.`
                    : "No goal yet, so only the agencies you unlock below can brand theirs."}
                </p>
              </div>
            )}

            {data.mode !== "off" && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {unlockedCount} of {agencies.length} {agencies.length === 1 ? "agency" : "agencies"} can brand their agency
                  <span className="font-normal text-muted-foreground"> · production this month</span>
                </p>
                <div className="max-h-[28rem] divide-y overflow-y-auto rounded-lg border">
                  {agencies.map((agency) => {
                    const status = agency.canBrand
                      ? agency.decision?.by === "goal"
                        ? "Hit the goal"
                        : agency.decision?.by === "upline"
                          ? "Unlocked by you"
                          : "Unlocked"
                      : agency.decision?.state === "locked"
                        ? "Locked by you"
                        : "Locked";
                    const goalShare = data.mode === "goal" && data.goal ? Math.min(100, (agency.thisMonth / data.goal.value) * 100) : null;
                    return (
                      <div key={agency.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
                        <div className="min-w-[10rem] flex-1">
                          <p className="truncate text-sm font-medium">{agency.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {[agency.owners, agency.depth > 1 && agency.parentName ? `under ${agency.parentName}` : null]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <div className="w-44 space-y-1">
                          <p className="text-xs tabular-nums text-muted-foreground">
                            {amount(metric, agency.thisMonth)}
                            {goalShare !== null && data.goal ? ` of ${amount(data.goal.type, data.goal.value)}` : ""}
                          </p>
                          {goalShare !== null && <Progress value={goalShare} className="h-1.5" />}
                        </div>
                        <div className="w-28 text-xs">
                          <p className={`font-medium ${agency.canBrand ? "text-emerald-700" : "text-gray-500"}`}>{status}</p>
                          {agency.canBrand && agency.hasBrand && <p className="text-muted-foreground">Own brand set</p>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-20"
                          disabled={busy !== null}
                          onClick={() => decide(agency, agency.canBrand ? "locked" : "unlocked")}
                        >
                          {busy === agency.id ? <Loader2 className="h-4 w-4 animate-spin" /> : agency.canBrand ? "Lock" : "Unlock"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
